import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { FONT_MONO, FONT_SERIF } from '../../views/landing/landingData';
import { domainsService, type DnsInstruction, type DomainOrderView, type DomainRow, type RegistrantContact, type SearchResult } from '../../services/domains.service';
import { BrandBtn, CloseX, DangerNote, Eyebrow, GhostBtn, Icons, Modal, Spinner } from './studioUi';
import { RecordsTable, SmallBtn, StatusPill, fmtDate, fmtIDR, payDomainOrder } from './domainUi';
import { useConfirm } from '../common/ConfirmDialog';
import { useToast } from '../common/Toast';

interface Props {
    open: boolean;
    onClose: () => void;
    applicationId: string;
    /** Fired whenever a domain becomes live/detached so the shell can refresh its published state. */
    onChanged?: () => void;
}

type Tab = 'connect' | 'buy';

const field: React.CSSProperties = { width: '100%', height: 44, padding: '0 14px', borderRadius: 12, border: '1px solid var(--hv-line)', background: 'var(--hv-card-2)', color: 'var(--hv-ink)', fontSize: 13.5, outline: 'none' };
const label: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--hv-body)' };
const segBtn = (active: boolean): React.CSSProperties => ({ height: 32, padding: '0 16px', border: 0, borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', background: active ? 'var(--hv-card)' : 'transparent', color: active ? 'var(--hv-ink)' : 'var(--hv-muted)', boxShadow: active ? '0 2px 5px -2px var(--hv-shadow)' : 'none' });

/**
 * "Use your own domain" — opened from the Publish dialog. Two tabs:
 * Connect (a domain the user already owns: DNS records + verify) and Buy
 * (.com / .id through HyVay, paid in rupiah via Midtrans). The list on top
 * shows the domains attached to this project with their status.
 */
export const DomainDialog: React.FC<Props> = ({ open, onClose, applicationId, onChanged }) => {
    const { user } = useAuth();
    const confirm = useConfirm();
    const toast = useToast();
    const [tab, setTab] = useState<Tab>('connect');
    const [domains, setDomains] = useState<DomainRow[]>([]);
    const [buyEnabled, setBuyEnabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // connect
    const [domainInput, setDomainInput] = useState('');
    const [connecting, setConnecting] = useState(false);
    const [pending, setPending] = useState<{ domain: DomainRow; records: DnsInstruction[]; checks: { txt: boolean; apex: boolean; www: boolean } | null } | null>(null);
    const [verifying, setVerifying] = useState(false);

    // buy
    const [query, setQuery] = useState('');
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState<SearchResult[] | null>(null);
    const [picked, setPicked] = useState<SearchResult | null>(null);
    const [contact, setContact] = useState<RegistrantContact>({ firstName: '', lastName: '', email: '', phone: '+62', address: '', city: '', state: '', postalCode: '', country: 'ID' });
    const [ordering, setOrdering] = useState(false);
    const [order, setOrder] = useState<DomainOrderView | null>(null);
    // Set while paying on Midtrans' own page in another tab (studio can't host Snap).
    const [payUrl, setPayUrl] = useState<string | null>(null);
    const payAbort = useRef<AbortController | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await domainsService.list(applicationId);
            setDomains(r.domains);
            setBuyEnabled(r.buyEnabled);
            // Keep the pending-DNS panel open for the first domain still waiting.
            // Functional update: `pending` here is a stale closure (deps are only
            // [applicationId]), and the old `!pending` guard read it as null right
            // after verify() set fresh checks — wiping the checklist the moment it
            // appeared. Only seed the panel when nothing is showing yet.
            const waiting = r.domains.find((d) => d.status === 'PENDING_DNS');
            if (waiting) {
                const ins = await domainsService.instructions(waiting.id);
                setPending((cur) => cur ?? { domain: ins.domain, records: ins.records, checks: null });
            }
        } catch (e: any) {
            setError(e?.message || 'Could not load domains.');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId]);

    useEffect(() => {
        if (!open) return;
        setError(null); setOrder(null); setPicked(null); setResults(null);
        const full = (user?.fullName || '').trim().split(/\s+/);
        setContact((c) => ({ ...c, email: user?.email || c.email, firstName: c.firstName || full[0] || '', lastName: c.lastName || full.slice(1).join(' ') }));
        void load();
    }, [open, load, user]);

    const connect = async () => {
        if (!domainInput.trim() || connecting) return;
        setConnecting(true); setError(null);
        try {
            const r = await domainsService.connect(applicationId, domainInput.trim());
            setPending({ domain: r.domain, records: r.records, checks: null });
            setDomainInput('');
            await load();
            if (r.domain.status === 'ACTIVE') onChanged?.();
        } catch (e: any) {
            setError(e.message);
        } finally {
            setConnecting(false);
        }
    };

    const verify = async () => {
        if (!pending || verifying) return;
        setVerifying(true); setError(null);
        try {
            const r = await domainsService.verify(pending.domain.id);
            setPending(r.ok ? null : { domain: r.domain, records: r.records, checks: r.checks });
            await load();
            if (r.ok) onChanged?.();
        } catch (e: any) {
            setError(e.message);
        } finally {
            setVerifying(false);
        }
    };

    const detach = async (d: DomainRow) => {
        const external = d.source === 'EXTERNAL';
        const ok = await confirm({
            eyebrow: external ? 'DISCONNECT DOMAIN' : 'DETACH DOMAIN',
            title: <>{external ? 'Disconnect' : 'Detach'} <em>{d.name}</em>?</>,
            description: external
                ? 'Your site goes back to its hyvay.com address. You can connect the domain again later.'
                : 'The domain is detached from this project. You keep it and can attach it again later.',
            confirmLabel: external ? 'Disconnect' : 'Detach',
            busyLabel: external ? 'Disconnecting…' : 'Detaching…',
            onConfirm: async () => { await domainsService.disconnect(d.id); },
        });
        if (!ok) return;
        if (pending?.domain.id === d.id) setPending(null);
        toast.success(`${d.name} ${external ? 'disconnected' : 'detached'}.`);
        await load();
        onChanged?.();
    };

    const search = async () => {
        if (!query.trim() || searching) return;
        setSearching(true); setError(null); setPicked(null);
        try {
            const r = await domainsService.search(query.trim());
            setResults(r.results);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setSearching(false);
        }
    };

    const contactReady = ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'state', 'postalCode', 'country'].every((k) => String((contact as any)[k] || '').trim().length > 0);

    const buy = async () => {
        if (!picked || !contactReady || ordering) return;
        setOrdering(true); setError(null);
        payAbort.current = new AbortController();
        try {
            const start = await domainsService.createOrder({ domain: picked.domain, applicationId, years: 1, contact });
            const result = await payDomainOrder(start.token, start.order_id, (o) => setOrder(o), {
                redirectUrl: start.redirect_url,
                onRedirect: setPayUrl,
                signal: payAbort.current.signal,
            });
            setOrder(result);
            await load();
            if (result?.status === 'FULFILLED') { setPicked(null); setResults(null); setQuery(''); onChanged?.(); }
        } catch (e: any) {
            setError(e.message);
        } finally {
            setOrdering(false); setPayUrl(null); payAbort.current = null;
        }
    };

    const set = (k: keyof RegistrantContact) => (e: React.ChangeEvent<HTMLInputElement>) => setContact((c) => ({ ...c, [k]: e.target.value }));

    return (
        <Modal open={open} onClose={onClose} label="Your own domain" maxWidth={560}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '26px 26px 0' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <h2 style={{ margin: 0, fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 32, lineHeight: 1.05, letterSpacing: '-0.015em', color: 'var(--hv-ink)' }}>Your own domain</h2>
                    <p style={{ margin: '9px 0 0', fontSize: 13.5, lineHeight: 1.6, color: 'var(--hv-body)' }}>Connect a domain you already own, or buy a new .com or .id right here. HTTPS is included.</p>
                </div>
                <CloseX onClick={onClose} />
            </div>

            <div style={{ padding: '20px 26px 26px', display: 'flex', flexDirection: 'column', gap: 18 }}>
                {/* current domains */}
                {(loading && domains.length === 0) ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Spinner size={13} /><Eyebrow>LOADING DOMAINS…</Eyebrow></div>
                ) : domains.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <Eyebrow>THIS PROJECT</Eyebrow>
                        {domains.map((d) => (
                            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px 10px 14px', borderRadius: 14, background: 'var(--hv-card-2)', border: '1px solid var(--hv-line)' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontFamily: FONT_MONO, fontSize: 13, color: 'var(--hv-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</div>
                                    <div style={{ marginTop: 3, fontSize: 11.5, color: 'var(--hv-muted)' }}>
                                        {d.source === 'NAMESILO' ? (d.expiresAt ? `Bought through HyVay · renews ${fmtDate(d.expiresAt)}` : 'Bought through HyVay · not registered yet') : 'Connected'}{d.lastError && d.status !== 'ACTIVE' ? ` · ${d.lastError}` : ''}
                                    </div>
                                </div>
                                <StatusPill domain={d} />
                                {d.status === 'PENDING_DNS' && pending?.domain.id !== d.id && (
                                    <SmallBtn onClick={async () => { const ins = await domainsService.instructions(d.id); setPending({ domain: ins.domain, records: ins.records, checks: null }); setTab('connect'); }}>DNS records</SmallBtn>
                                )}
                                <SmallBtn danger onClick={() => void detach(d)} aria-label={`Detach ${d.name}`}>{Icons.x({ size: 12 })}</SmallBtn>
                            </div>
                        ))}
                    </div>
                )}

                {/* tabs */}
                <div style={{ display: 'inline-flex', alignSelf: 'flex-start', gap: 4, padding: 4, borderRadius: 999, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)' }}>
                    <button type="button" onClick={() => setTab('connect')} style={segBtn(tab === 'connect')}>Connect a domain</button>
                    {/* Temporarily off while we look for a cheaper registrar than NameSilo. */}
                    <button type="button" disabled title="Coming soon" style={{ ...segBtn(false), cursor: 'not-allowed', opacity: 0.6, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        Buy a domain
                        <span style={{ fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '0.12em', fontWeight: 700, padding: '2px 6px', borderRadius: 999, background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)' }}>BETA</span>
                    </button>
                </div>

                {error && <DangerNote>{error}</DangerNote>}

                {tab === 'connect' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {!pending && (
                            <>
                                <label style={label}>
                                    <Eyebrow>YOUR DOMAIN</Eyebrow>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <input value={domainInput} onChange={(e) => setDomainInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void connect()} placeholder="tokosaya.id" spellCheck={false} style={{ ...field, fontFamily: FONT_MONO, flex: 1 }} />
                                        <BrandBtn data-track="domain_connect" onClick={() => void connect()} disabled={!domainInput.trim() || connecting} style={{ height: 44, padding: '0 18px', fontSize: 13 }}>{connecting ? <Spinner size={14} light /> : 'Continue'}</BrandBtn>
                                    </div>
                                </label>
                                <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: 'var(--hv-body)' }}>Just two DNS records (a TXT and a CNAME) put your site live at www.your-domain with HTTPS. A third, optional record makes it answer without the www too.</p>
                            </>
                        )}
                        {pending && (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <Eyebrow>DNS RECORDS FOR</Eyebrow>
                                    <span style={{ fontFamily: FONT_MONO, fontSize: 13, color: 'var(--hv-ink)' }}>{pending.domain.name}</span>
                                    <div style={{ flex: 1 }} />
                                    <button type="button" onClick={() => setPending(null)} className="hv-h-underline" style={{ border: 0, background: 'transparent', fontSize: 12, fontWeight: 600, color: 'var(--hv-muted)', cursor: 'pointer' }}>Add a different domain</button>
                                </div>
                                <RecordsTable records={pending.records} checks={pending.checks} />
                                {pending.checks && pending.domain.lastError && (
                                    <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: 'var(--hv-danger)' }}>{pending.domain.lastError}</p>
                                )}
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <GhostBtn onClick={onClose}>Do it later</GhostBtn>
                                    <BrandBtn style={{ flex: 1.6 }} data-track="domain_verify" onClick={() => void verify()} disabled={verifying}>
                                        {verifying ? <><Spinner size={14} light /> Checking DNS…</> : <>Check DNS {Icons.arrow({})}</>}
                                    </BrandBtn>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {tab === 'buy' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {!buyEnabled ? (
                            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--hv-body)' }}>Buying domains is not available right now. You can still connect a domain you own.</p>
                        ) : order && order.status !== 'PENDING' ? (
                            <div style={{ padding: 16, borderRadius: 16, background: order.status === 'FULFILLED' ? 'var(--hv-mint)' : 'var(--hv-sand)', color: order.status === 'FULFILLED' ? 'var(--hv-mint-ink)' : 'var(--hv-sand-ink)' }}>
                                <div style={{ fontSize: 14, fontWeight: 700 }}>
                                    {order.status === 'FULFILLED' ? `${order.domain.name} is yours` : order.status === 'FAILED' ? 'Payment did not go through' : 'Payment received — finishing registration'}
                                </div>
                                <div style={{ marginTop: 5, fontSize: 12.5, lineHeight: 1.6 }}>
                                    {order.status === 'FULFILLED'
                                        ? `Registered until ${fmtDate(order.domain.expiresAt)} and pointed at HyVay. Publish or republish your site and it goes live at the new address within minutes. The registrar will email you to confirm your contact details — please do.`
                                        : order.status === 'FAILED' ? 'Nothing was charged. You can try again.' : 'Our team completes the registration shortly; you will get an email when it is ready.'}
                                </div>
                                <div style={{ marginTop: 12 }}><SmallBtn onClick={() => setOrder(null)}>Done</SmallBtn></div>
                            </div>
                        ) : !picked ? (
                            <>
                                <label style={label}>
                                    <Eyebrow>FIND A NAME</Eyebrow>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void search()} placeholder="tokosaya" spellCheck={false} style={{ ...field, fontFamily: FONT_MONO, flex: 1 }} />
                                        <BrandBtn data-track="domain_search" onClick={() => void search()} disabled={!query.trim() || searching} style={{ height: 44, padding: '0 18px', fontSize: 13 }}>{searching ? <Spinner size={14} light /> : 'Search'}</BrandBtn>
                                    </div>
                                </label>
                                {results && (
                                    <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid var(--hv-line)', background: 'var(--hv-card)' }}>
                                        {results.map((r) => (
                                            <div key={r.domain} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: '1px solid var(--hv-line)' }}>
                                                <span style={{ flex: 1, minWidth: 0, fontFamily: FONT_MONO, fontSize: 13.5, color: r.available ? 'var(--hv-ink)' : 'var(--hv-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.domain}</span>
                                                {r.available && r.priceIdr != null ? (
                                                    <>
                                                        <span style={{ textAlign: 'right' }}>
                                                            <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: 'var(--hv-ink)' }}>{fmtIDR(r.priceIdr)}<span style={{ fontWeight: 400, color: 'var(--hv-muted)' }}> / year</span></span>
                                                            {r.renewIdr != null && r.renewIdr !== r.priceIdr && <span style={{ display: 'block', fontSize: 11, color: 'var(--hv-muted)' }}>renews at {fmtIDR(r.renewIdr)}</span>}
                                                        </span>
                                                        <BrandBtn onClick={() => setPicked(r)} style={{ height: 34, padding: '0 14px', fontSize: 12.5 }}>Buy</BrandBtn>
                                                    </>
                                                ) : (
                                                    <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.12em', color: 'var(--hv-muted)' }}>{r.premium ? 'PREMIUM · NOT SOLD HERE' : 'TAKEN'}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 14, background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)' }}>
                                    <span style={{ flex: 1, fontFamily: FONT_MONO, fontSize: 13.5 }}>{picked.domain}</span>
                                    <span style={{ fontSize: 13.5, fontWeight: 700 }}>{fmtIDR(picked.priceIdr || 0)} / 1 year</span>
                                    <button type="button" onClick={() => setPicked(null)} className="hv-h-underline" style={{ border: 0, background: 'transparent', fontSize: 12, fontWeight: 600, color: 'var(--hv-mint-ink)', cursor: 'pointer' }}>Change</button>
                                </div>
                                <Eyebrow>REGISTRANT (THE DOMAIN IS REGISTERED IN YOUR NAME)</Eyebrow>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                    <label style={label}>First name<input value={contact.firstName} onChange={set('firstName')} style={field} /></label>
                                    <label style={label}>Last name<input value={contact.lastName} onChange={set('lastName')} style={field} /></label>
                                    <label style={label}>Email<input type="email" value={contact.email} onChange={set('email')} style={field} /></label>
                                    <label style={label}>Phone<input value={contact.phone} onChange={set('phone')} placeholder="+62812345678" style={field} /></label>
                                    <label style={{ ...label, gridColumn: '1 / -1' }}>Street address<input value={contact.address} onChange={set('address')} style={field} /></label>
                                    <label style={label}>City<input value={contact.city} onChange={set('city')} style={field} /></label>
                                    <label style={label}>Province / state<input value={contact.state} onChange={set('state')} style={field} /></label>
                                    <label style={label}>Postal code<input value={contact.postalCode} onChange={set('postalCode')} style={field} /></label>
                                    <label style={label}>Country (2-letter)<input value={contact.country} onChange={(e) => setContact((c) => ({ ...c, country: e.target.value.toUpperCase().slice(0, 2) }))} maxLength={2} style={{ ...field, fontFamily: FONT_MONO }} /></label>
                                </div>
                                <p style={{ margin: 0, fontSize: 12, lineHeight: 1.6, color: 'var(--hv-muted)' }}>
                                    Domain registrations cannot be cancelled or refunded once completed. Your details go to the registrar as required by ICANN; public WHOIS privacy is on by default. Paid in rupiah via Midtrans, separately from your credits.
                                </p>
                                {payUrl && ordering && order?.status !== 'PAID' && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 14, border: '1px solid var(--hv-line)', background: 'var(--hv-page-2)', fontSize: 12.5, lineHeight: 1.55, color: 'var(--hv-body)' }}>
                                        <span style={{ flex: 1 }}>Finish paying in the Midtrans tab — this page updates by itself once the payment arrives.</span>
                                        <a href={payUrl} target="_blank" rel="noopener noreferrer" className="hv-h-underline" style={{ flex: 'none', fontWeight: 700, color: 'var(--hv-ink)' }}>Open payment page</a>
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: 10 }}>
                                    {payUrl && ordering
                                        ? <GhostBtn onClick={() => payAbort.current?.abort()}>Cancel</GhostBtn>
                                        : <GhostBtn onClick={() => setPicked(null)} disabled={ordering}>Back</GhostBtn>}
                                    <BrandBtn style={{ flex: 1.6 }} data-track="domain_buy" onClick={() => void buy()} disabled={!contactReady || ordering}>
                                        {ordering ? <><Spinner size={14} light /> {order?.status === 'PAID' ? 'Registering…' : 'Waiting for payment…'}</> : <>Pay {fmtIDR(picked.priceIdr || 0)} {Icons.arrow({})}</>}
                                    </BrandBtn>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};
